import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  ArrowLeft, Droplets, Loader2, AlertTriangle, 
  Check, Target, BarChart3, Edit2, Save, Users
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { TIER_NAMES } from '@/branding';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FluidBalanceProgramPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('fluid-target');
  const [loading, setLoading] = useState(true);
  
  // Fluid target state
  const [fluidTarget, setFluidTarget] = useState(null);
  const [editingTarget, setEditingTarget] = useState(false);
  const [newTarget, setNewTarget] = useState('');
  const [savingTarget, setSavingTarget] = useState(false);
  const [dailyConsumption, setDailyConsumption] = useState(null);
  
  // Weekly analysis state
  const [weeklyAnalysis, setWeeklyAnalysis] = useState(null);
  const [loadingWeekly, setLoadingWeekly] = useState(false);
  

  useEffect(() => {
    fetchFluidTarget();
    fetchDailyConsumption();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeTab === 'weekly-analysis') {
      fetchWeeklyAnalysis();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchFluidTarget = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/myinsul/fluid-balance/target`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFluidTarget(response.data.target_ml);
      setNewTarget(response.data.target_ml?.toString() || '');
    } catch (error) {
      console.error('Error fetching fluid target:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDailyConsumption = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/myinsul/fluid-balance/daily-consumption`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDailyConsumption(response.data);
    } catch (error) {
      console.error('Error fetching daily consumption:', error);
    }
  };

  const saveFluidTarget = async () => {
    if (!newTarget || isNaN(newTarget) || parseInt(newTarget) <= 0) {
      toast.error('Please enter a valid fluid target');
      return;
    }
    
    setSavingTarget(true);
    try {
      await axios.post(`${API_URL}/api/myinsul/fluid-balance/target`, 
        { target_ml: parseInt(newTarget) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFluidTarget(parseInt(newTarget));
      setEditingTarget(false);
      toast.success('Fluid target saved successfully');
      fetchDailyConsumption(); // Refresh to get updated comparison
    } catch (error) {
      toast.error('Failed to save fluid target');
    } finally {
      setSavingTarget(false);
    }
  };

  const fetchWeeklyAnalysis = async () => {
    setLoadingWeekly(true);
    try {
      const response = await axios.get(`${API_URL}/api/myinsul/fluid-balance/weekly-analysis`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWeeklyAnalysis(response.data);
    } catch (error) {
      toast.error('Failed to load weekly analysis');
    } finally {
      setLoadingWeekly(false);
    }
  };


  const tabs = [
    { id: 'fluid-target', label: 'My Fluid Target', icon: Target },
    { id: 'weekly-analysis', label: 'My Weekly Fluid Analysis', icon: BarChart3 }
  ];

  // Calculate percentage for pie chart
  const consumedPercentage = fluidTarget && dailyConsumption?.total_ml 
    ? Math.min((dailyConsumption.total_ml / fluidTarget) * 100, 100) 
    : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-blue-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-lg border-b border-border/50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/myinsul')}
                className="rounded-full"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center">
                  <Droplets className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
                    Fluid Balance Program
                  </h1>
                  <p className="text-xs text-muted-foreground">Fluid Intake Management</p>
                </div>
              </div>
            </div>
            <span className="text-xs bg-cyan-100 text-cyan-700 px-3 py-1 rounded-full font-medium">
              {TIER_NAMES.cdm}
            </span>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Tab Navigation */}
        <div className="flex flex-wrap gap-2 mb-8">
          {tabs.map((tab) => (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? 'default' : 'outline'}
              onClick={() => setActiveTab(tab.id)}
              className={`rounded-full ${
                activeTab === tab.id 
                  ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white border-0' 
                  : 'border-cyan-200 text-cyan-700 hover:bg-cyan-50'
              }`}
              data-testid={`${tab.id}-tab`}
            >
              <tab.icon className="w-4 h-4 mr-2" />
              {tab.label}
            </Button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          {/* My Fluid Target Tab */}
          {activeTab === 'fluid-target' && (
            <motion.div
              key="fluid-target"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <div className="bg-white rounded-3xl shadow-lg p-8">
                <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
                  <Target className="w-6 h-6 text-cyan-600" />
                  My Fluid Target
                </h2>

                {/* Set/Edit Fluid Target */}
                <div className="bg-cyan-50 rounded-2xl p-6 mb-8 border border-cyan-200">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-lg">Daily Fluid Restriction</h3>
                    {!editingTarget && fluidTarget && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => setEditingTarget(true)}
                        className="rounded-full"
                      >
                        <Edit2 className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                    )}
                  </div>
                  
                  {editingTarget || !fluidTarget ? (
                    <div className="flex items-end gap-4">
                      <div className="flex-1">
                        <Label htmlFor="fluid-target">Fluid Target (ml)</Label>
                        <Input
                          id="fluid-target"
                          type="number"
                          placeholder="e.g., 1500"
                          value={newTarget}
                          onChange={(e) => setNewTarget(e.target.value)}
                          className="mt-1"
                        />
                      </div>
                      <Button 
                        onClick={saveFluidTarget}
                        disabled={savingTarget}
                        className="rounded-full bg-gradient-to-r from-cyan-500 to-blue-500"
                      >
                        {savingTarget ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                        Save
                      </Button>
                      {editingTarget && (
                        <Button 
                          variant="outline"
                          onClick={() => {
                            setEditingTarget(false);
                            setNewTarget(fluidTarget?.toString() || '');
                          }}
                          className="rounded-full"
                        >
                          Cancel
                        </Button>
                      )}
                    </div>
                  ) : (
                    <div className="text-center">
                      <span className="text-4xl font-bold text-cyan-600">{fluidTarget}</span>
                      <span className="text-xl text-muted-foreground ml-2">ml/day</span>
                    </div>
                  )}
                </div>

                {/* Daily Consumption vs Target */}
                {fluidTarget && (
                  <div className="flex flex-col md:flex-row items-center gap-8">
                    {/* Pie Chart */}
                    <div className="relative w-64 h-64">
                      <svg className="w-full h-full transform -rotate-90">
                        <circle
                          cx="128"
                          cy="128"
                          r="100"
                          stroke="#e5e7eb"
                          strokeWidth="24"
                          fill="none"
                        />
                        <circle
                          cx="128"
                          cy="128"
                          r="100"
                          stroke={consumedPercentage >= 100 ? '#ef4444' : consumedPercentage >= 80 ? '#f59e0b' : '#06b6d4'}
                          strokeWidth="24"
                          fill="none"
                          strokeLinecap="round"
                          strokeDasharray={`${consumedPercentage * 6.28} 628`}
                        />
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className="text-4xl font-bold">{dailyConsumption?.total_ml || 0}</span>
                        <span className="text-muted-foreground">ml consumed</span>
                        <span className="text-sm text-muted-foreground mt-1">
                          {consumedPercentage.toFixed(0)}% of target
                        </span>
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="flex-1 space-y-4">
                      <div className={`p-4 rounded-2xl border ${
                        consumedPercentage >= 100 ? 'bg-red-50 border-red-200' :
                        consumedPercentage >= 80 ? 'bg-amber-50 border-amber-200' :
                        'bg-emerald-50 border-emerald-200'
                      }`}>
                        <div className="flex items-center gap-2">
                          {consumedPercentage < 100 ? (
                            <Check className="w-5 h-5 text-emerald-600" />
                          ) : (
                            <AlertTriangle className="w-5 h-5 text-red-600" />
                          )}
                          <span className={`font-semibold ${
                            consumedPercentage >= 100 ? 'text-red-600' :
                            consumedPercentage >= 80 ? 'text-amber-600' :
                            'text-emerald-600'
                          }`}>
                            {consumedPercentage >= 100 ? 'Exceeded Target!' :
                             consumedPercentage >= 80 ? 'Approaching Limit' :
                             'Within Target'}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          Remaining: <strong>{Math.max(0, fluidTarget - (dailyConsumption?.total_ml || 0))}ml</strong>
                        </p>
                      </div>

                      <div className="bg-gray-50 rounded-xl p-4">
                        <p className="text-sm text-muted-foreground">Today's Drinks/Fluids</p>
                        <p className="text-2xl font-bold">{dailyConsumption?.entry_count || 0} entries</p>
                      </div>
                    </div>
                  </div>
                )}

                {!fluidTarget && (
                  <div className="text-center py-8 text-muted-foreground">
                    <Droplets className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <p>Set your daily fluid restriction above to start tracking</p>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Weekly Fluid Analysis Tab */}
          {activeTab === 'weekly-analysis' && (
            <motion.div
              key="weekly-analysis"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              {loadingWeekly ? (
                <div className="flex items-center justify-center py-20">
                  <div className="text-center">
                    <Loader2 className="w-12 h-12 animate-spin text-cyan-500 mx-auto mb-4" />
                    <p className="text-muted-foreground">Analyzing your weekly fluid intake...</p>
                  </div>
                </div>
              ) : !weeklyAnalysis?.has_data ? (
                <div className="bg-white rounded-3xl shadow-lg p-12 text-center">
                  <AlertTriangle className="w-16 h-16 mx-auto text-amber-500 mb-4" />
                  <h2 className="text-2xl font-bold mb-2">No Data Available</h2>
                  <p className="text-muted-foreground">
                    {weeklyAnalysis?.message || "No drink entries found. Start logging your drinks to see weekly analysis!"}
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Weekly Average */}
                  <div className="bg-white rounded-3xl shadow-lg p-8">
                    <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
                      <BarChart3 className="w-6 h-6 text-cyan-600" />
                      Weekly Fluid Analysis
                    </h2>

                    <div className="grid md:grid-cols-2 gap-6 mb-8">
                      <div className="bg-cyan-50 rounded-2xl p-6 border border-cyan-200">
                        <p className="text-sm text-muted-foreground mb-1">Daily Average</p>
                        <p className="text-4xl font-bold text-cyan-600">
                          {weeklyAnalysis.analysis?.daily_average_ml?.toFixed(0) || 0}
                          <span className="text-lg text-muted-foreground ml-2">ml</span>
                        </p>
                        {fluidTarget && (
                          <p className="text-sm mt-2">
                            {weeklyAnalysis.analysis?.daily_average_ml <= fluidTarget ? (
                              <span className="text-emerald-600">✓ Within your {fluidTarget}ml target</span>
                            ) : (
                              <span className="text-red-600">⚠ Exceeds your {fluidTarget}ml target</span>
                            )}
                          </p>
                        )}
                      </div>
                      <div className="bg-blue-50 rounded-2xl p-6 border border-blue-200">
                        <p className="text-sm text-muted-foreground mb-1">Weekly Total</p>
                        <p className="text-4xl font-bold text-blue-600">
                          {weeklyAnalysis.analysis?.weekly_total_ml?.toFixed(0) || 0}
                          <span className="text-lg text-muted-foreground ml-2">ml</span>
                        </p>
                        <p className="text-sm text-muted-foreground mt-2">
                          From {weeklyAnalysis.total_entries || 0} drink entries
                        </p>
                      </div>
                    </div>

                    {/* Fluid Type Breakdown */}
                    <h3 className="font-semibold text-lg mb-4">Fluid Type Breakdown</h3>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
                      {[
                        { key: 'water', label: 'Water', color: 'bg-cyan-500' },
                        { key: 'tea', label: 'Tea', color: 'bg-green-500' },
                        { key: 'coffee', label: 'Coffee', color: 'bg-amber-700' },
                        { key: 'alcohol', label: 'Alcohol', color: 'bg-purple-500' },
                        { key: 'other', label: 'Other', color: 'bg-gray-500' }
                      ].map((type) => (
                        <div key={type.key} className="bg-gray-50 rounded-xl p-4 text-center">
                          <div className={`w-8 h-8 rounded-full ${type.color} mx-auto mb-2`} />
                          <p className="text-2xl font-bold">
                            {weeklyAnalysis.analysis?.fluid_breakdown?.[type.key]?.toFixed(0) || 0}%
                          </p>
                          <p className="text-sm text-muted-foreground">{type.label}</p>
                        </div>
                      ))}
                    </div>

                    {/* Other Beverages Bar Chart */}
                    {weeklyAnalysis.analysis?.other_breakdown && (
                      <>
                        <h3 className="font-semibold text-lg mb-4">Other Beverages Breakdown</h3>
                        <div className="space-y-3">
                          {[
                            { key: 'juices', label: 'Juices', color: 'bg-orange-500' },
                            { key: 'carbonated', label: 'Carbonated Beverages', color: 'bg-blue-400' },
                            { key: 'caffeinated', label: 'Other Caffeinated Drinks', color: 'bg-red-500' }
                          ].map((type) => {
                            const percentage = weeklyAnalysis.analysis.other_breakdown?.[type.key] || 0;
                            return (
                              <div key={type.key}>
                                <div className="flex justify-between text-sm mb-1">
                                  <span>{type.label}</span>
                                  <span className="font-medium">{percentage.toFixed(0)}%</span>
                                </div>
                                <div className="h-6 bg-gray-200 rounded-full overflow-hidden">
                                  <div 
                                    className={`h-full ${type.color} rounded-full transition-all`}
                                    style={{ width: `${percentage}%` }}
                                  />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </motion.div>
          )}

        </AnimatePresence>
      </main>
    </div>
  );
};

export default FluidBalanceProgramPage;
